<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card class="gfd-card">

      <!-- Шапка -->
      <div class="fact-dialog__header gfd-header" :class="{ 'fact-dialog__header--storno': stornoMode }">
        <div class="fact-dialog__title">
          {{ stornoMode ? 'Корректировка по конструктиву (сторно)' : 'Факт по конструктиву' }}
        </div>
        <div class="fact-dialog__subtitle" v-if="groupData.date && groupData.groupRow">
          {{ groupData.groupRow.label }} — {{ fmtIsoToRu(groupData.date.iso) }}
        </div>
        <q-btn icon="close" flat round dense color="white" v-close-popup class="fact-dialog__close" />
      </div>

      <!-- Тело -->
      <div class="gfd-body">

        <!-- Загрузка -->
        <div v-if="groupData.loading" class="fact-dialog__loader">
          <q-spinner color="indigo-7" size="36px" />
        </div>

        <template v-else>

          <!-- Баннер сторно -->
          <div v-if="stornoMode" class="fact-storno-banner q-mx-sm q-mt-sm">
            <q-icon name="edit_note" size="18px" />
            Для отправленных в УСП работ будет создана корректирующая запись (сторно); для неотправленных — изменения применятся сразу
          </div>

          <!-- Процентный ползунок для всех работ (скрыт в режиме сторно) -->
          <div v-if="!stornoMode" class="gfd-pct-section">
            <div class="gfd-pct-header">
              <span class="gfd-pct-label">Выполнение по всем работам:</span>
            </div>
            <div
              class="fwork-bar gfd-group-bar"
              ref="gfdBar"
              @mousedown.prevent="onGroupBarMouseDown"
              @touchstart.prevent="onGroupBarTouchStart"
            >
              <div
                class="fwork-bar__fill"
                :style="{ width: Math.min(100, groupFactOverallPct) + '%' }"
              />
              <!-- Замок: только когда не перекрывает пилюлю (pill уходит вправо при ≥85%) -->
              <q-icon
                v-if="groupCumulativePct > 0 && groupFactOverallPct < 85"
                class="fwork-bar__lock"
                name="lock"
                size="12px"
                color="white"
                :style="{ left: groupLockIconPct + '%' }"
              />
              <div
                class="fwork-bar__pct-pill"
                :style="groupFactOverallPct >= 85
                  ? { right: '4px' }
                  : { left: 'calc(' + groupFactOverallPct + '% + 4px)' }"
              >
                <!-- Замок внутри пилюли когда оба справа -->
                <q-icon
                  v-if="groupCumulativePct > 0 && groupFactOverallPct >= 85"
                  name="lock"
                  size="12px"
                  class="gfd-pill-lock"
                />{{ groupFactOverallPct }}&nbsp;%
              </div>
            </div>
          </div>

          <!-- Нет работ -->
          <div v-if="!groupData.workItems.length" class="gfd-empty">
            <q-icon name="info" size="32px" color="grey-4" />
            <span>Нет дочерних работ</span>
          </div>

          <!-- Список работ -->
          <div v-else class="gfd-works-list">
            <div
              v-for="(item, idx) in groupData.workItems"
              :key="idx"
              class="gfd-work-item"
              :class="{
                'gfd-work-item--sent':     item.isSent,
                'gfd-work-item--complete': item.workData?.is_complete,
              }"
            >
              <!-- Имя работы -->
              <div class="gfd-work-name">
                <q-icon
                  name="build" size="11px"
                  :class="item.row.usp_work_guid ? 'node-icon--work' : 'node-icon--work node-icon--work-no-usp'"
                  class="q-mr-xs"
                />
                {{ item.row.label }}
                <q-badge v-if="item.isSent" color="green-7" outline label="УСП" class="q-ml-xs" />
                <q-badge v-if="item.workData?.is_complete" color="green-9" label="Завершено" class="q-ml-xs" />
              </div>

              <!-- ═══ Режим сторно: текущее / новое / разница ═══ -->
              <template v-if="stornoMode && item.isSent">
                <div class="gfd-work-row">
                  <div class="gfd-work-meta" v-if="item.workData">
                    <span class="gfd-work-plan">
                      Объём: <b>{{ round6(item.workData.plan_total) }}</b> {{ item.workData.unit }}
                    </span>
                  </div>
                  <div class="storno-field-row q-mt-xs">
                    <div class="storno-field">
                      <span class="storno-field__label">Текущий факт</span>
                      <q-input
                        :model-value="item.originalFactValue"
                        outlined dense readonly type="number"
                        class="storno-field__input"
                        bg-color="grey-2"
                        hide-bottom-space
                      />
                    </div>
                    <div class="storno-field">
                      <span class="storno-field__label">Новый факт</span>
                      <q-input
                        :model-value="item.newFactValue"
                        @update:model-value="(v) => onGroupStornoNewFactInput(item, v)"
                        outlined dense type="number" min="0"
                        class="storno-field__input"
                        hide-bottom-space
                      />
                    </div>
                    <div class="storno-field storno-field--diff">
                      <span class="storno-field__label">Разница</span>
                      <div class="storno-diff-value" :class="{
                        'storno-diff-value--positive': item.stornoDiff > 0,
                        'storno-diff-value--negative': item.stornoDiff < 0,
                        'storno-diff-value--zero':     item.stornoDiff === 0,
                      }">
                        {{ item.stornoDiff > 0 ? '+' : '' }}{{ round6(item.stornoDiff) }}
                      </div>
                    </div>
                    <span class="storno-mat-unit" v-if="item.workData">{{ item.workData.unit }}</span>
                  </div>
                </div>

                <!-- Материалы в режиме сторно -->
                <div v-if="item.workData?.materials?.length" class="gfd-mats">
                  <div class="gfd-mats__title">Материалы</div>
                  <div v-for="(mat, mi) in item.workData.materials" :key="mi" class="gfd-mat-item">
                    <div class="gfd-mat-name">{{ mi + 1 }}. {{ mat.name }}</div>
                    <div class="storno-mat-row">
                      <div class="storno-mat-col">
                        <span class="storno-mat-col__label">Текущее</span>
                        <q-input
                          :model-value="getGroupOriginalMatValue(item, mat)"
                          outlined dense readonly type="number"
                          class="storno-mat-col__input"
                          bg-color="grey-2"
                          hide-bottom-space
                        />
                      </div>
                      <div class="storno-mat-col">
                        <span class="storno-mat-col__label">Новое</span>
                        <q-input
                          :model-value="getGroupStornoMatNewValue(item, mat)"
                          @update:model-value="(v) => onGroupStornoMatInput(item, mat, v)"
                          outlined dense type="number" min="0"
                          class="storno-mat-col__input"
                          hide-bottom-space
                        />
                      </div>
                      <div class="storno-mat-col storno-mat-col--diff">
                        <span class="storno-mat-col__label">Разница</span>
                        <div class="storno-diff-value storno-diff-value--sm" :class="{
                          'storno-diff-value--positive': getGroupStornoMatDiff(item, mat) > 0,
                          'storno-diff-value--negative': getGroupStornoMatDiff(item, mat) < 0,
                          'storno-diff-value--zero':     getGroupStornoMatDiff(item, mat) === 0,
                        }">
                          {{ getGroupStornoMatDiff(item, mat) > 0 ? '+' : '' }}{{ round6(getGroupStornoMatDiff(item, mat)) }}
                        </div>
                      </div>
                      <span class="storno-mat-unit">{{ mat.unit }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- ═══ Обычный режим ═══ -->
              <template v-else>
              <!-- Объём + ввод факта -->
              <div class="gfd-work-row">
                <div class="gfd-work-meta" v-if="item.workData">
                  <span class="gfd-work-plan">
                    Объём: <b>{{ round6(item.workData.plan_total) }}</b> {{ item.workData.unit }}
                  </span>
                  <span v-if="item.workData.cumulative_fact > 0" class="gfd-work-cum">
                    Выполнено ранее: <b>{{ round6(item.workData.cumulative_fact) }}</b>
                  </span>
                  <span class="gfd-work-rem">
                    Остаток: <b>{{ round6(item.workData.remainder) }}</b>
                  </span>
                </div>

                <div class="gfd-work-input-row">
                  <q-input
                    :model-value="item.factValue"
                    @update:model-value="(v) => onGroupItemValueInput(item, v)"
                    outlined dense
                    hide-bottom-space
                    type="number"
                    min="0"
                    placeholder="0"
                    class="gfd-work-input"
                    :readonly="item.isSent || item.workData?.is_complete"
                    :bg-color="item.isSent || item.workData?.is_complete ? 'green-1' : ''"
                    :max="item.workData && !item.isSent && !item.workData?.is_complete
                      ? Math.max(0, r6((item.workData.plan_total || 0) - (item.workData.cumulative_fact || 0)))
                      : undefined"
                    :hint="item.workData && !item.isSent && !item.workData?.is_complete
                      ? `Макс по остатку: ${round6(Math.max(0, r6((item.workData.plan_total || 0) - (item.workData.cumulative_fact || 0))))}` : ''"
                  />
                  <span v-if="item.workData" class="gfd-work-unit">{{ item.workData.unit }}</span>
                </div>
              </div>

              <!-- Прогресс-бар -->
              <div v-if="item.workData && item.workData.plan_total > 0" class="gfd-work-bar">
                <div
                  class="gfd-work-bar__fill"
                  :style="{ width: Math.min(100, item.factPct) + '%' }"
                  :class="{ 'gfd-work-bar__fill--over': item.factPct > 100 }"
                />
                <span class="gfd-work-bar__pct">{{ item.factPct }}%</span>
              </div>

              <!-- Материалы работы -->
              <div v-if="item.workData?.materials?.length" class="gfd-mats">
                <div class="gfd-mats__title">Материалы</div>
                <div
                  v-for="(mat, mi) in item.workData.materials"
                  :key="mi"
                  class="gfd-mat-item"
                  :class="{ 'gfd-mat-item--manual': mat.isManual }"
                >
                  <div class="gfd-mat-name">
                    <span>{{ mi + 1 }}. {{ mat.name }}</span>
                    <q-badge
                      v-if="mat.isManual"
                      color="deep-orange-5" outline label="вручную"
                      class="gfd-mat-badge"
                    />
                  </div>
                  <div class="gfd-mat-row">
                    <q-input
                      :model-value="mat.value"
                      @update:model-value="(v) => onGroupMatInput(item, mat, v)"
                      outlined dense type="number" min="0"
                      :max="materialZhcpMax(mat)"
                      class="gfd-mat-input"
                      :readonly="!mat.isEdit || item.isSent || item.workData.is_complete"
                      :bg-color="item.isSent || item.workData.is_complete ? 'green-1' : !mat.isEdit ? 'grey-2' : mat.isManual ? 'orange-1' : ''"
                    />
                    <span class="gfd-mat-unit">{{ mat.unit }}</span>
                    <q-btn
                      v-if="mat.isEdit && !item.isSent && !item.workData.is_complete && mat.isManual"
                      icon="refresh" flat round dense size="xs"
                      color="deep-orange-5"
                      @click="resetGroupMaterial(item, mat)"
                    >
                      <q-tooltip>Вернуть стандартный расчёт</q-tooltip>
                    </q-btn>
                  </div>
                  <div class="gfd-mat-info">
                    Норма: <b>{{ mat.norm }}</b>
                    <span v-if="mat.norm > 0"> = {{ item.factValue || 0 }} × {{ mat.norm }}</span>
                    &nbsp;·&nbsp; Остаток: <b>{{ round6(mat.remainder) }}</b> {{ mat.unit }}
                  </div>
                </div>
              </div>
              </template>

            </div>
          </div>

        </template>
      </div>

      <!-- Футер -->
      <div class="fact-dialog__footer">
        <template v-if="stornoMode">
          <q-btn
            label="Сохранить корректировку"
            color="deep-orange" unelevated no-caps
            class="fact-dialog__save-btn"
            :loading="groupData.saving"
            :disable="groupData.loading || !groupData.workItems.length"
            @click="$emit('save-storno')"
          />
        </template>
        <template v-else>
          <q-btn
            v-if="groupFactDialogHasSavableWorks"
            label="Сохранить все"
            color="primary" unelevated no-caps
            class="fact-dialog__save-btn"
            :loading="groupData.saving"
            :disable="groupData.loading || !groupData.workItems.length"
            @click="$emit('save')"
          />
        </template>
        <q-btn label="Отменить" flat no-caps color="grey-7" v-close-popup />
      </div>

    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';
import {
  computeMaxFactValue,
  applyNewFactInput,
  applyMaterialInput,
  getMaterialDiff,
  getMaterialNewValue,
  getOriginalMaterialValue,
  recalcStornoMaterialsByFact,
} from '../composables/useStornoEditing';

export default {
  name: 'PpmGroupFactDialog',
  props: {
    modelValue: Boolean,
    groupData: { type: Object, required: true },
    groupCumulativePct: { type: Number, default: 0 },
    groupFactOverallPct: { type: Number, default: 0 },
    groupLockIconPct: { type: Number, default: 0 },
    groupFactDialogHasSavableWorks: { type: Boolean, default: false },
    stornoMode: { type: Boolean, default: false },
    exceedAllowed: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'save', 'save-storno'],
  setup() {
    const { fmtIsoToRu, round6, r6, parseFactNum } = useFormatters();
    return { fmtIsoToRu, round6, r6, parseFactNum };
  },
  methods: {
    _groupBarPctFromEvent(e) {
      const bar = this.$refs.gfdBar;
      if (!bar) return null;
      const rect = bar.getBoundingClientRect();
      const x    = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
    },

    onGroupBarMouseDown(e) {
      const pct = this._groupBarPctFromEvent(e);
      if (pct !== null) this.onGroupPctChange(pct);
      const onMove = (ev) => {
        const p = this._groupBarPctFromEvent(ev);
        if (p !== null) this.onGroupPctChange(p);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup',   onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup',   onUp);
    },

    onGroupBarTouchStart(e) {
      const pct = this._groupBarPctFromEvent(e);
      if (pct !== null) this.onGroupPctChange(pct);
      const onMove = (ev) => {
        const p = this._groupBarPctFromEvent(ev);
        if (p !== null) this.onGroupPctChange(p);
      };
      const onEnd = () => {
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend',  onEnd);
      };
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend',  onEnd);
    },

    onGroupPctChange(pct) {
      pct = Math.max(this.groupCumulativePct, Math.min(100, pct));

      const items     = this.groupData.workItems;
      const totalPlan = items.reduce((s, i) => s + (i.workData?.plan_total || 0), 0);
      if (totalPlan <= 0) return;

      const targetTotal = this.r6((pct / 100) * totalPlan);

      const getMin = (i) => (i.workData?.cumulative_fact || 0) + (i.minFactValue || 0);

      let extra = Math.max(0, targetTotal - items.reduce((s, i) => s + getMin(i), 0));

      const editable  = items.filter((i) => !i.isSent && !i.workData?.is_complete && i.workData);
      const started   = editable.filter((i) => getMin(i) > 0);
      const unstarted = editable.filter((i) => getMin(i) === 0);

      editable.forEach((i) => { i._nv = getMin(i); });

      if (extra > 0 && started.length) {
        const startedCap = started.reduce((s, i) => s + Math.max(0, (i.workData.plan_total || 0) - getMin(i)), 0);
        if (startedCap > 0) {
          const fill = Math.min(extra, startedCap);
          started.forEach((i) => {
            const cap = Math.max(0, (i.workData.plan_total || 0) - getMin(i));
            i._nv = this.r6(getMin(i) + fill * (cap / startedCap));
          });
          extra = Math.max(0, extra - fill);
        }
      }

      if (extra > 0 && unstarted.length) {
        const unstartedCap = unstarted.reduce((s, i) => s + (i.workData.plan_total || 0), 0);
        if (unstartedCap > 0) {
          unstarted.forEach((i) => {
            const cap = i.workData.plan_total || 0;
            i._nv = this.r6(extra * (cap / unstartedCap));
          });
        }
      }

      editable.forEach((item) => {
        const plan         = item.workData.plan_total      || 0;
        const cumulative   = item.workData.cumulative_fact || 0;
        const minF         = item.minFactValue || 0;
        const totalDone    = Math.max(getMin(item), Math.min(plan, item._nv ?? getMin(item)));
        delete item._nv;
        const maxFactToday = Math.max(0, this.r6(plan - cumulative));
        let factToday      = Math.max(minF, this.r6(totalDone - cumulative));
        if (factToday > maxFactToday) factToday = maxFactToday;
        item.factValue          = String(this.r6(factToday));
        item.factPct            = plan > 0
          ? Math.min(100, Math.round(((cumulative + factToday) / plan) * 100))
          : 0;
        item.workData.remainder = Math.max(0, this.r6(plan - cumulative - factToday));
        this._recalcGroupItemMaterials(item, factToday);
      });
    },

    _recalcGroupItemMaterials(item, factNum) {
      const workPlan = item.workData?.plan_total || 0;
      (item.workData?.materials || []).forEach((m) => {
        if (!m.isManual) {
          const raw = workPlan > 0
            ? this.r6(m.plan_amount * (factNum / workPlan))
            : this.r6(factNum * m.norm);
          m.value = this.clampMaterialToZhcpRemainder(m, raw, false);
        }
      });
    },

    onGroupItemValueInput(item, val) {
      if (item.isSent || item.workData?.is_complete) return;
      if (item.workData) {
        const plan         = item.workData.plan_total      || 0;
        const cumulative   = item.workData.cumulative_fact || 0;
        const minF         = item.minFactValue || 0;
        const maxFactToday = Math.max(0, this.r6(plan - cumulative));
        let num            = this.parseFactNum(val);
        if (Number.isNaN(num) || num < 0) num = 0;
        if (num < minF) num = minF;
        if (num > maxFactToday) {
          num = maxFactToday;
          this.$q?.notify?.({
            type:    'warning',
            message: 'Не больше остатка по работе (план минус выполнено ранее)',
          });
        }
        item.factValue = String(this.r6(num));
        item.factPct   = plan > 0 ? Math.min(100, Math.round(((cumulative + num) / plan) * 100)) : 0;
        item.workData.remainder = Math.max(0, this.r6(plan - cumulative - num));
        this._recalcGroupItemMaterials(item, num);
      } else {
        item.factValue = String(this.parseFactNum(val));
      }
    },

    onGroupMatInput(item, mat, val) {
      if (item.isSent || item.workData?.is_complete) return;
      mat.value    = this.clampMaterialToZhcpRemainder(mat, val, true);
      mat.isManual = true;
    },

    resetGroupMaterial(item, mat) {
      const num      = this.parseFactNum(item.factValue);
      const workPlan = item.workData?.plan_total || 0;
      const raw      = workPlan > 0
        ? this.r6(mat.plan_amount * (num / workPlan))
        : this.r6(num * mat.norm);
      mat.value    = this.clampMaterialToZhcpRemainder(mat, raw, false);
      mat.isManual = false;
    },

    materialZhcpMax(mat) {
      if (mat?.remainder == null || mat.remainder === '') return undefined;
      return Math.max(0, this.parseFactNum(mat.remainder));
    },

    groupStornoMaxFactValue(item) {
      return computeMaxFactValue({
        plan:          item.workData?.plan_total,
        cumulative:    item.workData?.cumulative_fact,
        exceedAllowed: this.exceedAllowed,
        r6:            this.r6,
      });
    },

    onGroupStornoNewFactInput(item, val) {
      const { newFactValue, stornoDiff } = applyNewFactInput({
        val,
        originalValue: item.originalFactValue,
        maxVal:        this.groupStornoMaxFactValue(item),
        parseFactNum:  this.parseFactNum,
        r6:            this.r6,
        notify:        (o) => this.$q?.notify?.(o),
      });
      item.newFactValue = newFactValue;
      item.stornoDiff   = stornoDiff;
      recalcStornoMaterialsByFact({
        newFactValue,
        originalMaterials: item.originalMaterials,
        workMaterials:     item.workData?.materials,
        workPlan:          item.workData?.plan_total,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
      });
    },

    getGroupOriginalMatValue(item, mat) {
      return getOriginalMaterialValue(item.originalMaterials, mat);
    },

    getGroupStornoMatNewValue(item, mat) {
      return getMaterialNewValue(item.originalMaterials, mat);
    },

    getGroupStornoMatDiff(item, mat) {
      return getMaterialDiff({
        originalMaterials: item.originalMaterials,
        mat,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
      });
    },

    onGroupStornoMatInput(item, mat, val) {
      applyMaterialInput({
        val,
        mat,
        originalMaterials: item.originalMaterials,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
        notify:            (o) => this.$q?.notify?.(o),
      });
    },

    clampMaterialToZhcpRemainder(mat, rawVal, notifyIfClamped) {
      let num = this.parseFactNum(rawVal);
      if (num < 0) num = 0;
      if (mat?.remainder == null || mat.remainder === '') return String(this.r6(num));
      const rem = this.parseFactNum(mat.remainder);
      const cap = this.r6(Math.max(0, rem));
      if (num > cap + 1e-6) {
        if (notifyIfClamped) {
          this.$q?.notify?.({
            type:    'warning',
            message: `Не больше остатка по ЖЦП: ${this.round6(cap)} ${mat.unit || ''}`.trim(),
          });
        }
        return String(cap);
      }
      return String(this.r6(num));
    },
  },
};
</script>
